import { Controller } from "@hotwired/stimulus";

// Generic controller for managing nested form items (abilities, etc.)
// Connects to data-controller="nested-items"
export default class extends Controller {
	static targets = ["template", "list", "item"];
	static values = {
		abilities: Array,
		formName: String,
		prefill: Object,
		lines: Object,
	};

	connect() {
		// Render existing abilities from JSON data
		// A card posted with a reference to the parent's ability comes back as a changed prefilled card.
		this.abilitiesValue.forEach((ability) => {
			const item = this.renderAbility(ability);
			if (ability.source_ability_id) item.dataset.prefilled = item.dataset.edited = "true";
		});
		this.updatePositions();
	}

	add(event) {
		event.preventDefault();
		this.renderAbility({
			name: "",
			description: "",
			position: this.itemTargets.length,
			persisted: false,
		});
		this.updatePositions();
	}

	// A specialization starts from its parent's abilities: the cards the author has not touched follow the
	// parent, the ones they edited stay as their own.
	kindChanged(event) {
		const form = event.target.form;
		const name = (field) => `${this.formNameValue}[${field}]`;
		if (!form || ![name("parent_character_class_id"), name("class_kind")].includes(event.target.name)) return;

		for (const item of this.itemTargets.filter((card) => card.dataset.prefilled)) {
			if (!item.dataset.edited) {
				item.remove();
				continue;
			}
			delete item.dataset.prefilled;
			item.querySelector("input[name*='[source_ability_id]']").value = "";
			item.querySelector("[data-role='note']").hidden = true;
		}

		const kind = form.querySelector(`[name='${name("class_kind")}']:checked`)?.value;
		const parent = form.querySelector(`select[name='${name("parent_character_class_id")}']`)?.value;
		if (kind === "specialization") {
			for (const card of this.prefillValue[parent] ?? []) this.renderAbility(card).dataset.prefilled = "true";
		}
		this.fillLines(form, kind === "specialization" ? (this.linesValue[parent] ?? {}) : {});
		this.updatePositions();
	}

	// The parent's equipment lines fill the ones the author has not typed over; a line they changed stays.
	fillLines(form, lines) {
		for (const name of ["weapons_description", "armors_description", "magic_items_description"]) {
			const input = form.querySelector(`[name='${this.formNameValue}[${name}]']`);
			if (!input || input.value !== (input.dataset.prefilled ?? "")) continue;
			input.value = input.dataset.prefilled = lines[name] ?? "";
		}
	}

	// Marks a card the author changed; the example-level picker and the drag handle are not changes.
	edited(event) {
		const target = event.target;
		const change = event.type === "click" ? target.closest("[data-rule] button") : !target.matches("[data-class-rules-target='viewLevel']");
		if (change) event.currentTarget.dataset.edited = "true";
	}

	remove(event) {
		event.preventDefault();
		const item = event.target.closest(`[data-${this.identifier}-target='item']`);

		// If the item has an ID (persisted record), mark it for destruction
		const idField = item.querySelector("input[name*='[id]']");
		if (idField?.value) {
			const destroyField = item.querySelector("input[name*='[_destroy]']");
			if (destroyField) {
				destroyField.value = "1";
			}
			item.style.display = "none";
			// A hidden field that is still invalid would block the submit; read-only ones are skipped by validation.
			for (const field of item.querySelectorAll("input, textarea")) field.readOnly = true;
		} else {
			// If it's a new record, just remove it from the DOM
			item.remove();
		}

		this.updatePositions();
	}

	// Called by sortable controller when drag ends
	updatePositions() {
		this.itemTargets.forEach((item, index) => {
			if (item.style.display !== "none") {
				const positionField = item.querySelector("input[name*='[position]']");
				if (positionField) {
					positionField.value = index;
				}
			}
		});
	}

	renderAbility(ability) {
		// Clone the template
		const template = this.templateTarget.content.cloneNode(true);
		const item = template.querySelector(`[data-${this.identifier}-target='item']`);

		// Replace NEW_RECORD with empty brackets for array-based params
		item.innerHTML = item.innerHTML.replace(/\[NEW_RECORD\]/g, "[]");

		// Generic field population - iterate over all ability properties
		for (const [key, value] of Object.entries(ability)) {
			if (key === "id" || key === "persisted") continue;
			// An object has no single input; the item's own controller reads it back from here.
			if (value && typeof value === "object") {
				item.dataset[key.replace(/_(\w)/g, (_, c) => c.toUpperCase())] = JSON.stringify(value);
				continue;
			}

			const input = item.querySelector(`input[name*='[${key}]']`);
			const textarea = item.querySelector(`textarea[name*='[${key}]']`);
			const select = item.querySelector(`select[name*='[${key}]']`);

			if (input) input.value = value ?? "";
			if (textarea) textarea.value = value ?? "";
			if (select) select.value = value ?? "";
		}

		if (ability.note) {
			const note = item.querySelector("[data-role='note']");
			note.textContent = ability.note;
			note.hidden = false;
		}

		// Add ID field if this is a persisted record
		if (ability.persisted && ability.id) {
			const idInput = document.createElement("input");
			idInput.type = "hidden";
			idInput.name = `${this.formNameValue}[abilities_attributes][][id]`;
			idInput.value = ability.id;
			item.appendChild(idInput);
		}

		this.listTarget.appendChild(item);
		return item;
	}
}
